import { lazy, Suspense, useEffect, useMemo, useRef, useState, type FC, type RefObject } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createSelector } from '@reduxjs/toolkit';

import { _isEmpty, _isNil, _isNotEmpty, _notNil } from '@/littledash';

import type { Animal } from '@/model/Animal.model';
import type { PlannedAnimalDefinition } from './PlannedAnimals/PlannedAnimals.utils';
import type { TreatmentGroup } from '@/model/TreatmentGroup.model';
import type { AnimalMapV1 } from '@/model/PlannedAnimal.model';
import type { StudyApiId } from '@/model/Study.model';

import { errorToast } from '@/helpers';
import { ExceptionHandler } from '@/utils/ExceptionHandler';
import InVivoError from '@/model/InVivoError';
import { featuresSelector } from '@/support/Selectors';
import { useApiHook } from '@/support/Hooks/api/useApiHook';
import type { CustomGridSettings } from '@/components/UI/SpreadSheet/SpreadSheet.model';
import { spreadSheetValidationTooltip } from '@/components/UI/SpreadSheet/SpreadSheet.utils';
import type { HotTableRef } from '@/components/UI/SpreadSheet/HotTableV16';

import Button from '@/components/UI/Button';
import Loading from '@/components/Loading';
import Switch from '@/components/UI/FormElements/Switch';
import ApiErrorBanner from '@/components/ApiErrorBanner';

import {
  type MapAnimalRow,
  transformAnimalsToMapData,
  mapAnimalsSettings,
  updateCellMeta,
  processDataChange,
  createAnimalMappings,
  mapAnimalsColumnData,
} from './MapAnimals.utils';
import './PlannedAnimals.scss';

import type HotTable from '@handsontable//react';
import type HandsontableV12 from 'handsontable';
import type HandsontableV16 from 'handsontable-v16';

type HotTableRefUnion = HotTable | { hotInstance: HandsontableV16 | null } | null;

const SpreadSheet = lazy(() => import('@/components/UI/SpreadSheet'));
const MapAnimalsTableV16 = lazy(() => import('./MapAnimalsTableV16'));

interface MapAnimalsProps {
  studyApiId: StudyApiId;
  plannedAnimals: Array<PlannedAnimalDefinition>;
  groups: Array<TreatmentGroup>;
  onSubmit: (mappedAnimals: AnimalMapV1) => Promise<void>;
}

const hotUpgradeSelector = createSelector([featuresSelector], (features) => ({
  hotUpgrade: features?.hot_upgrade ?? false,
}));

const MapAnimals: FC<MapAnimalsProps> = ({ studyApiId, plannedAnimals, groups, onSubmit }) => {
  const { hotUpgrade } = useSelector(hotUpgradeSelector);
  const [isInvalid, setIsInvalid] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [apiError, setApiError] = useState<unknown | null>(null);
  const [mapData, setMapData] = useState<Array<MapAnimalRow>>([]);
  const [overrideNames, setOverrideNames] = useState<boolean>(false);
  const [animalsData, setAnimalsData] = useState<Array<Animal>>([]);

  const ref = useRef<HotTableRefUnion>(null);
  const dispatch = useDispatch();

  const { invoke: fetchAnimals } = useApiHook({
    endpoint: 'GET /api/v1/studies/{studyId}/animals',
    path: { studyId: studyApiId },
    query: { perPage: -1, include: ['study_group', 'cage'] },
    invokeOnInit: false,
    options: { onError: { throw: false, capture: true, toast: false, slug: 'fetch-animals' } },
  });

  useEffect(() => {
    const loadAnimals = async () => {
      try {
        const response = await fetchAnimals();
        if (response.type === 'success') {
          const animals = ((response?.body as unknown as { data: Array<Animal> })?.data ?? []).filter(
            (animal) => !animal.animal_definition_api_id && _isNotEmpty(animal.study_group)
          );
          setAnimalsData(animals);
        }
      } catch (error) {
        setApiError(error);
      } finally {
        setIsLoading(false);
      }
    };

    loadAnimals();
  }, []);

  useEffect(() => {
    if (_isNotEmpty(animalsData)) {
      const transformedData = transformAnimalsToMapData(animalsData, plannedAnimals);
      setMapData(transformedData);
    }
  }, [animalsData, plannedAnimals]);

  useEffect(() => {
    const hot = ref.current?.hotInstance;
    if (_notNil(hot) && _isNotEmpty(mapData) && !hotUpgrade) {
      updateCellMeta(hot as HandsontableV12, mapData, groups, plannedAnimals);
    }
  }, [mapData, groups, plannedAnimals, hotUpgrade]);

  const columnData = useMemo(() => {
    if (hotUpgrade) {
      return null;
    }
    return mapAnimalsColumnData(groups, ref as RefObject<HotTable>);
  }, [groups, hotUpgrade]);

  const settings = useMemo(() => {
    if (hotUpgrade || _isNil(columnData)) {
      return null;
    }
    return mapAnimalsSettings(columnData);
  }, [columnData, hotUpgrade]);

  const closeModal = () => {
    dispatch({
      type: 'CLOSE_MODAL',
    });
  };

  const handleV12DataChange = (changes: Array<unknown> | null, source: string) => {
    if (_isNil(changes) || source === 'loadData') {
      return;
    }
    const hotInstance = ref.current?.hotInstance ?? null;
    if (_notNil(hotInstance)) {
      const updatedMapData = processDataChange(hotInstance as HandsontableV12, plannedAnimals);
      setMapData(updatedMapData);
    }
  };

  const handleV16DataChange = (updatedData: Array<MapAnimalRow>) => {
    setMapData(updatedData);
  };

  const submitMapping = async () => {
    if (!isInvalid && !isSubmitting && _notNil(onSubmit)) {
      try {
        setIsSubmitting(true);
        const mappedAnimals = createAnimalMappings(mapData);
        if (_isNotEmpty(mappedAnimals)) {
          await onSubmit({
            mappings: mappedAnimals,
            override_names: overrideNames,
          });
        }
        closeModal();
      } catch (err) {
        errorToast('Could not submit mapping request');
        ExceptionHandler.captureException(
          new InVivoError('Could not map planned animals', {
            cause: err,
            slug: 'map-animals-modal-error',
          })
        );
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  if (_isEmpty(animalsData)) {
    return (
      <div
        data-test-component="EmptyAnimals"
        data-test-element="container"
        className="flex flex-wrap overflow-hidden w-50 center ma3 bg-light-gray br2 mt4 mb-2 relative"
      >
        <div className="pa4">
          <div className="text-center">
            {isLoading ? (
              <Loading txt="Loading animals..." />
            ) : (
              <>
                <h3 className="mb2">No animals found</h3>
                <p className="mb3">Registered animals must be assigned to groups before mapping planned animals.</p>
                <Button plain onClick={closeModal}>
                  Close
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (_isEmpty(plannedAnimals)) {
    return (
      <div className="mapContainer" data-test-component="EmptyPlannedAnimals" data-test-element="container">
        <div className="pa4">
          <div className="text-center">
            <h3 className="mb2">No planned animals found</h3>
            <p className="mb3">There are no planned animals available to map from.</p>
            <Button plain onClick={closeModal}>
              Close
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mapContainer" data-test-component="MapAnimals" data-test-element="container">
      <div className="mapContentWrapper">
        <div className="mapHeaderSection">
          {apiError ? (
            <ApiErrorBanner
              className="mw6 mb4"
              error={apiError}
              title="There was an error fetching animals data"
              text="An error has occurred when fetching animals data, please try again. If this keeps occurring please contact support."
            />
          ) : null}
          <h3 className="normal f4 lh-title">Map animals to planned animals</h3>
          <p className="lh-copy f5 pt2">
            Map existing animals to planned animals by selecting an available planned animal ID. Animals are
            pre-filtered to match group and sex with planned animals.
          </p>
        </div>
        <div className="ph4 pb4 flex justify-between items-center">
          <div className="flex items-center">
            <Switch
              testId="overrideNames"
              value={overrideNames}
              onChange={() =>
                setOverrideNames((prev) => {
                  return !prev;
                })
              }
            />
            <label className="ml2 mb0">Overwrite animal names with planned IDs</label>
          </div>
        </div>
        <div
          className="ow-spreadsheet-styles mapSpreadsheetContainer"
          data-test-component="MapAnimals"
          data-test-element="spreadsheet-container"
        >
          <Suspense fallback={<Loading />}>
            {hotUpgrade ? (
              <MapAnimalsTableV16
                mapData={mapData}
                setMapData={setMapData}
                groups={groups}
                plannedAnimals={plannedAnimals}
                innerRef={ref as RefObject<HotTableRef>}
                setInvalid={setIsInvalid}
                onDataChange={handleV16DataChange}
              />
            ) : (
              <SpreadSheet
                data={mapData}
                settings={settings as CustomGridSettings}
                innerRef={ref as RefObject<HotTable>}
                className="mapAnimals"
                buttonClasses={'absolute-sheet-buttons'}
                setInvalid={setIsInvalid}
                afterChange={handleV12DataChange}
                addCol={false}
              />
            )}
          </Suspense>
        </div>
      </div>
      <div
        className="ph4 pv3 flex self-end bt b--moon-gray w-100"
        data-test-component="MapAnimals"
        data-test-element="action-container"
      >
        <Button
          onClick={submitMapping}
          testId="save-action"
          disabled={isInvalid || isSubmitting}
          tooltip={isInvalid ? spreadSheetValidationTooltip : undefined}
        >
          Map Animals
        </Button>
        <Button plain className="ml3" onClick={closeModal} testId="cancel-action">
          Cancel
        </Button>
      </div>
    </div>
  );
};

export default MapAnimals;
